import * as React from 'react';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Link } from 'react-router-dom';

import { saveLastVma } from '../Allures/useVma.ts';
import { PARAMETRES_ALLURES_MODIFIES_EVENT } from '../Allures/useAllureTable.ts';
import { type RecordData } from '../Allures/RecordData.tsx';

// Libelles des types de record (cf AlluresEnum cote serveur / RecordForm).
const LIBELLES_RECORD: Record<string, string> = {
  MARATHON: 'Marathon',
  SEUIL: 'Semi-marathon',
  DIX: '10 km',
  CINQ: '5 km',
  TROIS: '3 km',
  VMA: 'VMA',
  SOUTENU: 'Steady',
  ENDURANCE: 'Endurance',
  RECUPERATION: 'Récupération',
};

export function libelleRecord(r: RecordData): string {
  return `${LIBELLES_RECORD[r.type] ?? r.type} en ${formaterTempsRecord(r)}`;
}

function formaterTempsRecord(r: RecordData): string {
  const mm = String(r.minute).padStart(2, '0');
  const ss = String(r.second).padStart(2, '0');
  return r.hour > 0 ? `${r.hour}h${mm}'${ss}"` : `${r.minute}'${ss}"`;
}

function lire<T>(url: string): Promise<T | null> {
  return fetch(new Request(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + sessionStorage.getItem('token'),
    },
  })).then((response) => {
    if (response.status === 200) return response.json() as Promise<T>;
    if (response.status === 404) return null; // rien d'enregistre
    throw new Error('statut ' + response.status);
  });
}

// Icone d'information : explique au survol comment la VMA est calculee.
// `emplacementParametres` : ou se trouvent les parametres d'allures par
// rapport au bloc (ex: "ci-dessous" sur la page Mes preferences).
export function InfoVma({ emplacementParametres = 'ci-dessous' }: { emplacementParametres?: React.ReactNode }) {
  return (
    <Tooltip
      arrow
      // Infobulle survolable, pour pouvoir cliquer sur le lien "Allures".
      disableInteractive={false}
      title={
        <span>
          La VMA est calculée à partir du dernier record saisi sur la page{' '}
          <Link to="/allures" style={{ color: 'inherit', fontWeight: 'bold' }}>Allures</Link> et du %VMA de la zone de
          ce record dans les paramètres {emplacementParametres}. Pour la changer, saisis un nouveau record.
        </span>
      }
    >
      <InfoOutlinedIcon
        fontSize="small"
        color="action"
        aria-label="Comment la VMA est calculée"
        sx={{ cursor: 'help', verticalAlign: 'middle', ml: 0.5 }}
      />
    </Tooltip>
  );
}

export type UseVmaRecord = {
  /** VMA (km/h, arrondie au dixieme), ou null si aucune n'est enregistree. */
  vma: number | null;
  /** Record dont la VMA est deduite, ou null. */
  record: RecordData | null;
  chargement: boolean;
  erreur: string | null;
};

// VMA de reference de l'utilisateur (deduite de son record) et ce record,
// rechargees apres une modification des parametres d'allures. Partage par la
// page Mes preferences et le tableau de bord de l'accueil (Main.tsx).
export function useVmaRecord(): UseVmaRecord {
  const [vma, setVma] = React.useState<number | null>(null);
  const [record, setRecord] = React.useState<RecordData | null>(null);
  const [chargement, setChargement] = React.useState(true);
  const [erreur, setErreur] = React.useState<string | null>(null);

  const charger = React.useCallback(() => {
    Promise.all([lire<number>('/api/preference/vma'), lire<RecordData>('/api/preference/record')])
      .then(([valeur, dernierRecord]) => {
        const arrondie = valeur === null ? null : Math.round(valeur * 10) / 10;
        setVma(arrondie);
        setRecord(dernierRecord);
        setErreur(null);
        if (arrondie !== null) saveLastVma(arrondie);
      })
      .catch((e: Error) => setErreur('Impossible de récupérer la VMA (' + e.message + ')'))
      .finally(() => setChargement(false));
  }, []);

  React.useEffect(() => {
    charger();
    // Le %VMA de la zone du record a pu changer dans les parametres
    // d'allures : le serveur a alors recalcule la VMA.
    window.addEventListener(PARAMETRES_ALLURES_MODIFIES_EVENT, charger);
    return () => window.removeEventListener(PARAMETRES_ALLURES_MODIFIES_EVENT, charger);
  }, [charger]);

  return { vma, record, chargement, erreur };
}

// Affichage (lecture seule) de la VMA de reference de l'utilisateur, deduite
// du record saisi sur la page Allures et du %VMA de la zone de ce record
// dans les parametres d'allures. Elle n'est pas modifiable directement :
// pour la changer, saisir un nouveau record sur la page Allures.
export default function VmaRecord() {
  const { vma, record, chargement, erreur } = useVmaRecord();

  return (
    <Box id="preference-vma" sx={{ mt: 4 }}>
      <h2>VMA <InfoVma /></h2>

      {erreur && <Alert severity="error" sx={{ mb: 2 }}>{erreur}</Alert>}

      {!chargement && !erreur && vma === null && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Aucune VMA n'est encore enregistrée pour ton compte : saisis un record sur la page{' '}
          <Link to="/allures">Allures</Link> pour la calculer.
        </Alert>
      )}

      {vma !== null && (
        <Paper variant="outlined" sx={{ p: 2, display: 'inline-block', minWidth: 280 }}>
          <Typography variant="h4" component="p">
            {vma.toLocaleString('fr-FR')} km/h
          </Typography>
          {record && (
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              Déduite de ton record : {libelleRecord(record)}
            </Typography>
          )}
        </Paper>
      )}
    </Box>
  );
}
